import { Vital } from 'shared/api';

export const VITAL_LAST_UPDATE_KEY = 'Last update';

export const FILTERED_VITAL_NAMES = {
  BLOOD_PRESSURE: 'Blood Pressure',
  PULSE_OXIMETRY: 'Pulse Oximetry',
  TEMPERATURE: 'Temperature',
  GLUCOSE: 'Glucose',
  WEIGHT: 'Weight',
  HEIGHT: 'Height',
  PEAK_FLOW_RATE: 'Peak Flow Rate',
  MANUAL_NOTES: 'Manual Notes',
} as const;

export type FilteredVitalFdata = {
  Value?: number;
  Unit?: string;
  VisitReason?: string;
  GeneralNotes?: string;
  Sys?: number;
  Dia?: number;
  [VITAL_LAST_UPDATE_KEY]?: string;
};

export const parseVitalFdata = (fdata: unknown): FilteredVitalFdata | null => {
  if (!fdata) {
    return null;
  }

  if (typeof fdata === 'string') {
    try {
      return JSON.parse(fdata) as FilteredVitalFdata;
    } catch {
      return null;
    }
  }

  if (typeof fdata === 'object') {
    return fdata as FilteredVitalFdata;
  }

  return null;
};

export const getVitalLastUpdate = (fdata: unknown): Date | null => {
  const parsed = parseVitalFdata(fdata);
  const lastUpdate = parsed?.[VITAL_LAST_UPDATE_KEY];

  if (!lastUpdate) {
    return null;
  }

  const date = new Date(lastUpdate);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const compareVitalsByLastUpdateDesc = (a: Vital, b: Vital): number => {
  const aTime = getVitalLastUpdate(a.fdata)?.getTime() ?? 0;
  const bTime = getVitalLastUpdate(b.fdata)?.getTime() ?? 0;

  return bTime - aTime;
};

export const groupFilteredVitalsByName = (
  vitals: Array<Vital | null | undefined>
): Map<string, Vital[]> => {
  const groups = new Map<string, Vital[]>();

  vitals.forEach((vital) => {
    if (!vital?.name) {
      return;
    }

    const existing = groups.get(vital.name) ?? [];
    groups.set(vital.name, [...existing, vital]);
  });

  return groups;
};

export const getLatestVitalPerName = (
  vitals: Array<Vital | null | undefined>
): Vital[] =>
  [...groupFilteredVitalsByName(vitals).values()].map(
    (groupVitals) => [...groupVitals].sort(compareVitalsByLastUpdateDesc)[0]
  );

const VITAL_GROUP_ORDER = [
  FILTERED_VITAL_NAMES.BLOOD_PRESSURE,
  FILTERED_VITAL_NAMES.GLUCOSE,
  FILTERED_VITAL_NAMES.TEMPERATURE,
  FILTERED_VITAL_NAMES.WEIGHT,
  FILTERED_VITAL_NAMES.HEIGHT,
  FILTERED_VITAL_NAMES.PEAK_FLOW_RATE,
  FILTERED_VITAL_NAMES.MANUAL_NOTES,
];

export const sortVitalGroupNames = (names: string[]): string[] => {
  const priority = new Map<string, number>(
    VITAL_GROUP_ORDER.map((name, index) => [name, index])
  );

  return [...names].sort((a, b) => {
    const aIndex = priority.get(a);
    const bIndex = priority.get(b);

    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    if (aIndex !== undefined) {
      return -1;
    }
    if (bIndex !== undefined) {
      return 1;
    }

    return a.localeCompare(b);
  });
};
